import React, { useMemo, useState } from 'react';
import { X, Plus } from 'lucide-react';
import SliderRow from 'shared/components/ui/SliderRow';
import DrawerTab from 'shared/components/ui/DrawerTab';
import BrandSelect from 'features/playground/components/BrandSelect';
import ProviderLogo from 'features/playground/components/ProviderLogo';
import JsonEditor from 'features/prompts/components/JsonEditor';

/**
 * ModelConfigPanel — the prompt's model block, editable two ways.
 *
 *   Configure — pick a provider you've actually linked, then a model from the
 *               synced catalog, then the generation knobs. No typing, no
 *               remembering which id the provider uses this month.
 *   JSON      — the raw object, for the cases the form can't express: a model
 *               your codebase names differently, an Azure deployment, a param
 *               the sliders don't cover.
 *
 * Both tabs edit the SAME object, so switching is lossless in either direction:
 * select in the form, flip to JSON and rename the model to match your codebase,
 * flip back and the sliders still reflect what's there. Nothing is normalised
 * or dropped on the way through — keys the form doesn't know about survive
 * untouched, which is what makes the round trip safe.
 */

// Penalties are an OpenAI-family concept; showing them for Anthropic would be
// offering a knob the provider ignores. Mirrors playground's ModelConfigPopup.
const OPENAI_FAMILY = new Set([
  'openai', 'azure_openai', 'openai_compat', 'groq', 'deepseek', 'fireworks', 'mistral', 'xai',
]);

// Everything the Configure tab knows how to render. Any other key in the object
// is untouched by the form and only visible in JSON.
const FORM_KEYS = new Set([
  'provider', 'model', 'temperature', 'max_tokens', 'top_p',
  'frequency_penalty', 'presence_penalty', 'stop',
]);

const labelCls = 'text-[12px] font-medium text-[color:var(--text-primary)]';
const clearCls = 'grid h-5 w-5 place-items-center rounded border border-[color:var(--border-default)] text-[color:var(--text-muted)] hover:text-[color:var(--text-primary)]';

export default function ModelConfigPanel({
  value, onChange, disabled, integrations, modelsForCredential,
}) {
  const [tab, setTab] = useState('form');
  const model = value && typeof value === 'object' && !Array.isArray(value) ? value : {};

  // Patch, never replace — unknown keys the JSON tab added must survive a
  // slider drag. A blank value deletes the key so it isn't sent to the provider.
  const patch = (p) => {
    const next = { ...model };
    for (const [k, v] of Object.entries(p)) {
      if (v === '' || v === null || v === undefined) delete next[k];
      else next[k] = v;
    }
    onChange(next);
  };

  // The credential backing the chosen provider. A prompt stores `provider`, not
  // a credential id — it's a template, and which key runs it is decided at run
  // time — so the credential here is only how we look up the model list.
  const credential = useMemo(
    () => (integrations || []).find((i) => i.provider === model.provider) || null,
    [integrations, model.provider],
  );

  const providerOptions = useMemo(() => {
    const seen = new Map();
    for (const i of integrations || []) {
      if (i?.provider && !seen.has(i.provider)) seen.set(i.provider, i);
    }
    return [...seen.keys()].map((p) => ({
      value: p,
      label: p,
      icon: <ProviderLogo provider={p} size={16} />,
    }));
  }, [integrations]);

  // A model set in JSON (or by a teammate) that the catalog doesn't list still
  // has to be selectable, or flipping to this tab would silently blank it.
  const modelOptions = useMemo(() => {
    const ids = credential ? [...(modelsForCredential?.(credential) || [])] : [];
    if (model.model && !ids.includes(model.model)) ids.unshift(model.model);
    return ids.map((m) => ({ value: m, label: m }));
  }, [credential, modelsForCredential, model.model]);

  const stops = Array.isArray(model.stop) ? model.stop : [];
  const isOpenAI = OPENAI_FAMILY.has(model.provider);
  // Surfaced so the form never looks like the whole truth when it isn't.
  const extraKeys = Object.keys(model).filter((k) => !FORM_KEYS.has(k));

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-1 border-b border-[color:var(--border-subtle)]">
        <DrawerTab id="form" label="Configure" active={tab === 'form'} onClick={setTab} />
        <DrawerTab id="json" label="JSON" active={tab === 'json'} onClick={setTab} />
      </div>

      {tab === 'json' ? (
        <JsonEditor
          value={model}
          emptyValue={{}}
          disabled={disabled}
          onChange={(next) => onChange(next)}
        />
      ) : (
        <div className="flex flex-col gap-3.5">
          {providerOptions.length === 0 ? (
            <p className="text-[11.5px] text-[color:var(--text-muted)]">
              No linked providers yet: add a key under Integrations, or use the JSON tab
              to set the provider and model by hand.
            </p>
          ) : (
            <div className="flex flex-wrap items-end gap-2">
              <label className="flex min-w-[150px] flex-col gap-1">
                <span className={labelCls}>Provider</span>
                <BrandSelect
                  value={model.provider || ''}
                  disabled={disabled}
                  minWidth={150}
                  placeholder="Select provider"
                  options={providerOptions}
                  // Model ids don't survive a provider change, so clear it rather
                  // than leaving one provider's id sitting under another's name.
                  onChange={(p) => patch({ provider: p, model: '' })}
                />
              </label>
              <label className="flex min-w-[210px] flex-1 flex-col gap-1">
                <span className={labelCls}>Model</span>
                <BrandSelect
                  value={model.model || ''}
                  disabled={disabled || !model.provider}
                  minWidth={210}
                  placeholder={model.provider ? 'Select model' : 'Pick a provider first'}
                  leadingIcon={model.provider ? <ProviderLogo provider={model.provider} size={16} /> : null}
                  options={modelOptions}
                  onChange={(m) => patch({ model: m })}
                />
              </label>
            </div>
          )}

          {/* The catalog lists what the PROVIDER publishes, not what this team's
              key is entitled to call: those are different sets, and we can't
              know the second without spending a request against the key. Saying
              so is cheaper and more honest than implying the list is verified. */}
          {model.provider && (
            <p className="-mt-1.5 text-[11px] text-[color:var(--text-muted)]">
              Your key may not have access to every listed model.
            </p>
          )}

          <SliderRow
            label="Temperature" value={model.temperature} min={0} max={2} step={0.1} disabled={disabled}
            onChange={(v) => patch({ temperature: v })} onClear={() => patch({ temperature: '' })}
          />
          <SliderRow
            label="Max tokens" value={model.max_tokens} min={0} max={16384} step={128} disabled={disabled}
            onChange={(v) => patch({ max_tokens: v })} onClear={() => patch({ max_tokens: '' })}
          />
          <SliderRow
            label="Top p" value={model.top_p} min={0} max={1} step={0.05} disabled={disabled}
            onChange={(v) => patch({ top_p: v })} onClear={() => patch({ top_p: '' })}
          />
          {isOpenAI && (
            <>
              <SliderRow
                label="Frequency penalty" value={model.frequency_penalty} min={-2} max={2} step={0.1} disabled={disabled}
                onChange={(v) => patch({ frequency_penalty: v })} onClear={() => patch({ frequency_penalty: '' })}
              />
              <SliderRow
                label="Presence penalty" value={model.presence_penalty} min={-2} max={2} step={0.1} disabled={disabled}
                onChange={(v) => patch({ presence_penalty: v })} onClear={() => patch({ presence_penalty: '' })}
              />
            </>
          )}

          <div className="flex flex-col gap-1.5">
            <div className="flex items-center justify-between">
              <span className={labelCls}>Stop sequences</span>
              <button
                type="button"
                disabled={disabled}
                onClick={() => patch({ stop: [...stops, ''] })}
                aria-label="Add stop sequence"
                title="Add stop sequence"
                className="grid h-5 w-5 place-items-center rounded border border-[color:var(--border-default)] text-[color:var(--text-muted)] hover:bg-surface-hover hover:text-[color:var(--text-primary)] disabled:opacity-40"
              >
                <Plus size={10} />
              </button>
            </div>
            {stops.map((s, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={i} className="flex items-center gap-1.5">
                <input
                  value={s}
                  disabled={disabled}
                  onChange={(e) => patch({ stop: stops.map((x, j) => (j === i ? e.target.value : x)) })}
                  placeholder="e.g. \n\n"
                  className="w-full h-7 rounded-md border border-[color:var(--border-default)] bg-surface px-2 font-mono text-[11.5px] text-[color:var(--text-primary)] focus:border-[color:var(--border-strong)] focus:outline-none"
                />
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => patch({ stop: stops.filter((_, j) => j !== i) })}
                  aria-label="Remove stop sequence"
                  title="Remove"
                  className={clearCls}
                >
                  <X size={10} />
                </button>
              </div>
            ))}
          </div>

          {extraKeys.length > 0 && (
            <p className="text-[11px] text-[color:var(--text-muted)]">
              Also set, editable in JSON: <span className="font-mono">{extraKeys.join(', ')}</span>
            </p>
          )}
        </div>
      )}
    </div>
  );
}
